import {Paths} from "@2d-front-monorepo/core";
import {useLayoutEffect, useMemo, useReducer, useRef} from "react";
import {IMvvmContextState, MvvmContextState, MvvmContextUnsubscribe} from "./MvvmContextState";
import {createTrackingProxy, SyntheticChannel} from "./MvvmTrackingProxy";

/**
 * Wraps a `MvvmContextState` in a tracking facade. Methods that read from the context
 * (state, getValue, getError, hasErrors, ...) record the paths they touch onto `onRead`
 * so the calling component can subscribe to exactly those paths.
 *
 * The facade is a Proxy over the underlying instance: methods that don't need tracking
 * (setValue, update, triggerValidation, registerValidation, handleSubmit, ...) flow
 * straight through. External mutations should still use setValue/update rather than
 * assigning directly to state. Class identity is preserved well enough for the existing
 * `(context as MvvmContextState<T>).foo()` casts in the codebase to keep working.
 */
// Marker property a tracking facade answers with its underlying raw context.
const MVVM_RAW: unique symbol = Symbol("mvvmRaw");

/**
 * Returns the raw MvvmContextState behind a tracking facade (or the context itself when it
 * is already raw). Controls subscribe to their own field precisely, so they must read the
 * raw context — reading through a host's facade would subscribe the HOST component to every
 * control-bound path and re-render it on each field change.
 */
export function unwrapMvvmContext<T extends object>(context: IMvvmContextState<T>): MvvmContextState<T> {
	return ((context as any)[MVVM_RAW] as MvvmContextState<T> | undefined) ?? (context as MvvmContextState<T>);
}

export function createMvvmFacade<T extends object>(
	raw: MvvmContextState<T>,
	onRead: (path: string) => void,
	proxyCache: WeakMap<object, Map<string, any>>
): IMvvmContextState<T> {
	const handler: ProxyHandler<MvvmContextState<T>> = {
		get(target: MvvmContextState<T>, prop: string | symbol, receiver: any): any {
			if (prop === MVVM_RAW) {
				return target;
			}

			switch (prop) {
				case "state": {
					// A late async callback can read through a facade whose context was disposed
					// (deps recreation); its state is gone and Proxy() over undefined throws.
					const state = target.state as object | undefined;
					if (state == null) return undefined;
					return createTrackingProxy(state, "", {
						onRead,
						// Object reads subscribe replacement-only, so an object passed along
						// opaquely (memo dep, child prop) still re-renders when replaced —
						// without regressing to re-render-per-keystroke on leaves below it.
						onObjectRead: path => onRead(SyntheticChannel.objectForPath(path)),
						// The proxy has already written the value and confirmed the change via
						// deepEquals, so going through setValue would no-op (its equality check
						// compares against the freshly-mutated state). Notify + validate directly.
						onWrite: path => target.enqueueValidation<T>(path as Paths<T>)
					}, proxyCache);
				}
				case "initStatus":
					onRead(SyntheticChannel.initStatus);
					return target.initStatus;
				case "initError":
					onRead(SyntheticChannel.initStatus);
					return target.initError;
				case "getValue":
					return (path: any) => {
						onRead(path);
						return target.getValue(path);
					};
				case "getError":
					// Every per-path error mutation notifies $err:<path>; the $errors channel is
					// only needed for aggregate reads (getErrors/hasErrors/isInvalid).
					return (path: any) => {
						onRead(SyntheticChannel.errorForPath(path));
						return target.getError(path);
					};
				case "getErrors":
					return () => {
						onRead(SyntheticChannel.errors);
						return target.getErrors();
					};
				case "hasErrors":
					return () => {
						onRead(SyntheticChannel.errors);
						return target.hasErrors();
					};
				case "isInvalid":
					return () => {
						onRead(SyntheticChannel.errors);
						return target.isInvalid();
					};
				case "getExceptions":
					return () => {
						onRead(SyntheticChannel.exceptions);
						return target.getExceptions();
					};
				case "hasExceptions":
					return () => {
						onRead(SyntheticChannel.exceptions);
						return target.hasExceptions();
					};
				case "isSubmitting":
					return () => {
						onRead(SyntheticChannel.submitting);
						return target.isSubmitting();
					};
				case "isValidating":
					return () => {
						onRead(SyntheticChannel.validating);
						return target.isValidating();
					};
				default: {
					const v = Reflect.get(target, prop, receiver);
					return typeof v === "function" ? v.bind(target) : v;
				}
			}
		}
	};
	return new Proxy(raw, handler) as unknown as IMvvmContextState<T>;
}

/**
 * Per-component facade with read-tracking subscription reconciliation. Reads during the
 * current render record paths into a per-render tracker; after commit, the effect diffs
 * the new reads against the active subscriptions and updates them — adding subscriptions
 * for newly-read paths, dropping subscriptions for paths no longer read.
 *
 * The forceRender callback is the only thing registered as a subscriber on the context;
 * when any subscribed path fires, the component re-renders and a fresh tracking pass runs.
 */
export function useMvvmTrackingFacade<T extends object>(context: MvvmContextState<T>): IMvvmContextState<T> {
	const [, forceRender] = useReducer(c => c + 1, 0);

	// Tracker swapped fresh every render so previous reads don't leak.
	const readsRef = useRef<Set<string>>(new Set());
	readsRef.current = new Set();

	// Active subscriptions keyed by path. The reconciler diffs against `readsRef.current`.
	const subsRef = useRef<Map<string, MvvmContextUnsubscribe>>(new Map());

	// Proxy cache stays stable across renders so the same raw object always maps to the
	// same proxy identity — important for downstream code that compares `state.x` by ref.
	const proxyCacheRef = useRef<WeakMap<object, Map<string, any>>>(undefined);
	proxyCacheRef.current ??= new WeakMap();

	// Facade is stable across renders for the same context: methods close over readsRef
	// (which is mutated, not replaced as a binding) and proxyCacheRef.current.
	const facade = useMemo(
		() => createMvvmFacade(context, path => readsRef.current.add(path), proxyCacheRef.current!),
		[context]
	);

	// The subscriptions belong to a specific context instance: when the context is recreated
	// (host deps change), every active subscription points at the disposed instance and must
	// be dropped so the reconciler below re-subscribes the same paths on the new one.
	const subscribedContextRef = useRef<MvvmContextState<T>>(context);

	// Layout effect, not passive: React runs a child's passive effects before its parent's,
	// so a control writing in its mount effect (MvvmSelect auto-select-first) would fire
	// before a passive subscription here existed and the host would miss the change. Every
	// layout effect runs before any passive effect, so subscriptions always precede those writes.
	useLayoutEffect(() => {
		const reads = readsRef.current;
		const subs = subsRef.current;

		if (subscribedContextRef.current !== context) {
			for (const unsub of subs.values()) {
				unsub();
			}
			subs.clear();
			subscribedContextRef.current = context;
		}

		for (const path of reads) {
			if (!subs.has(path)) {
				subs.set(path, context.subscribe(path, forceRender));
			}
		}

		for (const [path, unsub] of subs) {
			if (!reads.has(path)) {
				unsub();
				subs.delete(path);
			}
		}
	});

	useLayoutEffect(() => {
		const subs = subsRef.current;
		return () => {
			for (const unsub of subs.values()) {
				unsub();
			}
			subs.clear();
		};
	}, []);

	return facade;
}
